 import React from 'react';
import { motion } from 'framer-motion';
import { FiPackage, FiList, FiCheckCircle, FiLayers, FiGitBranch, FiTrendingUp, FiSettings, FiShield } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MobileFeatureCarousel from '../components/MobileFeatureCarousel';
import ImageModal from '../components/ImageModal';

const SBOM = () => {
  const [imageModal, setImageModal] = React.useState<{
    isOpen: boolean;
    src: string;
    alt: string;
    title?: string;
  }>({
    isOpen: false,
    src: '',
    alt: '',
    title: ''
  });

  const openImageModal = (src: string, alt: string, title?: string) => {
    setImageModal({ isOpen: true, src, alt, title });
  };

  const closeImageModal = () => {
    setImageModal({ isOpen: false, src: '', alt: '', title: '' });
  };

  const currentStats = [
    { label: "Total Scans", value: "27", description: "SBOM scans completed", icon: <FiPackage className="w-8 h-8" /> },
    { label: "Repositories Analyzed", value: "13", description: "repositories under SBOM analysis", icon: <FiGitBranch className="w-8 h-8" /> },
    { label: "Libraries Cataloged", value: "2,722", description: "software libraries identified", icon: <FiList className="w-8 h-8" /> },
    { label: "Applications Tracked", value: "46", description: "applications under management", icon: <FiLayers className="w-8 h-8" /> }
  ];

  const coreFeatures = [
    {
      title: "Automated Dependency Discovery",
      description: "Comprehensive identification of direct and transitive dependencies with precise version tracking and license detection.",
      icon: <FiPackage className="w-8 h-8" />,
      details: [
        "Direct Dependencies: Identification of directly declared dependencies",
        "Transitive Dependencies: Discovery of indirect dependency chains",
        "Version Tracking: Precise version identification for all components",
        "License Detection: Automatic license identification and compliance tracking"
      ]
    },
    {
      title: "Software Inventory Management",
      description: "Complete cataloging of software components with detailed metadata and relationship mapping for comprehensive asset management.",
      icon: <FiList className="w-8 h-8" />,
      details: [
        "Component Cataloging: Comprehensive cataloging of all software components",
        "Metadata Collection: Detailed metadata for each identified component",
        "Relationship Mapping: Mapping of component relationships and dependencies",
        "Asset Classification: Classification of components by type and criticality"
      ]
    },
    {
      title: "Supply Chain Visibility",
      description: "Enhanced visibility into software supply chain with vendor identification, origin tracking, and comprehensive risk assessment.",
      icon: <FiShield className="w-8 h-8" />,
      details: [
        "Vendor Identification: Identification of software vendors and suppliers",
        "Origin Tracking: Tracking of component origins and sources",
        "Update Monitoring: Monitoring of component updates and changes",
        "Risk Assessment: Assessment of supply chain security risks"
      ]
    }
  ];

  const sbomStandards = [
    {
      title: "SPDX (Software Package Data Exchange)",
      description: "Industry standard for software bill of materials",
      icon: <FiCheckCircle className="w-8 h-8" />,
      details: [
        "SPDX Format Support: Native support for SPDX SBOM format",
        "Standard Compliance: Full compliance with SPDX specifications",
        "Interoperability: Seamless integration with SPDX-compatible tools",
        "Export Capabilities: Export SBOMs in SPDX format"
      ]
    },
    {
      title: "CycloneDX",
      description: "Modern standard for software supply chain component analysis",
      icon: <FiSettings className="w-8 h-8" />,
      details: [
        "CycloneDX Format: Support for CycloneDX SBOM standard",
        "Vulnerability Integration: Integration with vulnerability databases",
        "Licensing Information: Comprehensive licensing data inclusion",
        "Tool Ecosystem: Compatibility with CycloneDX tool ecosystem"
      ]
    },
    {
      title: "SWID (Software Identification Tags)",
      description: "ISO standard for software identification",
      icon: <FiLayers className="w-8 h-8" />,
      details: [
        "SWID Tag Generation: Automatic generation of SWID tags",
        "ISO/IEC 19770-2 Compliance: Compliance with international standards",
        "Asset Management: Integration with IT asset management systems",
        "Lifecycle Tracking: Software lifecycle management capabilities"
      ]
    }
  ];

  const supportedEcosystems = [
    { name: "JavaScript/Node.js", managers: "npm, yarn package managers", icon: "🟨" },
    { name: "Python", managers: "pip, conda, poetry package managers", icon: "🐍" },
    { name: "Java", managers: "Maven, Gradle build systems", icon: "☕" },
    { name: ".NET", managers: "NuGet package manager", icon: "🔷" },
    { name: "Go", managers: "Go modules and vendor directories", icon: "🐹" },
    { name: "Ruby", managers: "Bundler and gem dependencies", icon: "💎" },
    { name: "PHP", managers: "Composer package manager", icon: "🐘" },
    { name: "Rust", managers: "Cargo package manager", icon: "🦀" }
  ];

  const managementFeatures = [
    {
      title: "Version Control",
      description: "Comprehensive version control for SBOM documents with change tracking and historical analysis capabilities.",
      icon: <FiGitBranch className="w-8 h-8" />,
      details: [
        "SBOM Versioning: Version control for SBOM documents",
        "Change Tracking: Tracking of changes in software components",
        "Historical Analysis: Historical analysis of component evolution",
        "Baseline Management: Management of SBOM baselines"
      ]
    },
    {
      title: "Lifecycle Management",
      description: "Complete lifecycle tracking with end-of-life monitoring and update notifications for all components.",
      icon: <FiTrendingUp className="w-8 h-8" />,
      details: [
        "Component Lifecycle: Tracking of component lifecycle stages",
        "End-of-Life Monitoring: Monitoring of component end-of-life status",
        "Update Notifications: Notifications for component updates",
        "Deprecation Tracking: Tracking of deprecated components"
      ]
    },
    {
      title: "Quality Assurance",
      description: "Robust quality assurance with SBOM validation, quality metrics, and comprehensive accuracy assessment.",
      icon: <FiCheckCircle className="w-8 h-8" />,
      details: [
        "SBOM Validation: Validation of SBOM accuracy and completeness",
        "Quality Metrics: Quality metrics for SBOM generation",
        "Accuracy Assessment: Assessment of SBOM accuracy",
        "Completeness Analysis: Analysis of SBOM completeness"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8 px-4 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Software Bill of Materials (SBOM)" />
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
            The SBOM module in Ana Security provides comprehensive software inventory management and dependency tracking capabilities. It creates detailed inventories of software components, libraries, and dependencies to enhance supply chain security and compliance management.
          </p>
          
          <div className="relative overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900 mb-8">
            <img 
              src="/screenshots/security-modules/05_sbom.png" 
              alt="SBOM Dashboard"
              className="w-full cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => openImageModal('/screenshots/security-modules/05_sbom.png', 'SBOM Dashboard', 'Software Bill of Materials Dashboard')}
            />
          </div>
        </div>

        {/* Current Statistics - Responsive */}
        <div className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SBOM Dashboard - Current Statistics
          </h2>
          
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {currentStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="text-center p-4 bg-gray-50 dark:bg-black rounded-lg"
              >
                <div className="text-primary-500 mb-3 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {stat.description}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden mb-8">
            <MobileFeatureCarousel 
              features={currentStats.map(stat => ({
                title: stat.label,
                description: stat.description,
                icon: stat.icon,
                details: [`Current Value: ${stat.value}`, stat.description]
              }))}
              autoPlay={true}
              autoPlayInterval={4000}
            />
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Dashboard Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>New Scan Button:</strong> Orange "New Scan" button for initiating new SBOM generation</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Project Filtering:</strong> "All" dropdown for filtering by specific projects</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Repository Links:</strong> Direct access to analyzed repositories</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Scan Date Tracking:</strong> Real-time tracking of scanning activities</span>
              </div>
            </div>
          </div>
        </div>

        {/* Core Features - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SBOM Core Features
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.details?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={coreFeatures}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* SBOM Standards Compliance - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SBOM Standards Compliance
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {sbomStandards.map((standard, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {standard.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {standard.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {standard.description}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {standard.details?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-black rounded-lg">
                      <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={sbomStandards}
              autoPlay={true}
              autoPlayInterval={7000}
            />
          </div>
        </div>

        {/* Supported Ecosystems - Responsive Grid */}
        <div className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Supported Ecosystems
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-6">
            SBOM supports comprehensive analysis across multiple programming ecosystems:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {supportedEcosystems.map((ecosystem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="p-4 bg-gray-50 dark:bg-black rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{ecosystem.icon}</span>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {ecosystem.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {ecosystem.managers}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Analysis Depth</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Surface Level:</strong> Basic dependency identification</span>
              </div>
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Deep Analysis:</strong> Comprehensive transitive dependency mapping</span>
              </div>
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Source Code Analysis:</strong> Analysis of source code for embedded components</span>
              </div>
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Binary Analysis:</strong> Analysis of compiled binaries and executables</span>
              </div>
            </div>
          </div>
        </div>

        {/* Management Features - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SBOM Management Features
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {managementFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {feature.details?.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-black rounded-lg">
                      <FiLayers className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={managementFeatures}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* Call to Action - Responsive */}
        <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Build Your Software Bill of Materials?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8">
            Start using SBOM to gain complete visibility into your software supply chain and enhance security compliance.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.a 
              href="/ana-actions" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiCheckCircle className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Use ANA Actions</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Automate SBOM management</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/dashboard" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiTrendingUp className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor your software inventory</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/integrations" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiSettings className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Setup Integrations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect your development tools</p>
              </div>
            </motion.a>
          </div>
        </div>
      </motion.div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        src={imageModal.src}
        alt={imageModal.alt}
        title={imageModal.title}
      />
    </div>
  );
};

export default SBOM;
